/** @jsxImportSource @emotion/react */
import { Error, SearchOff, } from '@mui/icons-material';
import { Box, Container, Grid, Stack, Typography, useTheme } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { stripedDateGrid } from '../../../common/CustumEmotionStyles';
import { DueDateWithColor, padLeadingZerosToNum, remToPixels } from '../../../common/CustomFunctions';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ApproverService from '../../../service/ApproverService';
import { format } from "date-fns";
import { nb } from "date-fns/locale";

interface IProps {
    type: "waitingYou" | "waitingOthers",
}

const DatagridWaitingInvoices = (props: IProps): JSX.Element => {

    const theme = useTheme();
    const [gridRows, setGridRows] = useState<any[]>([]);
    const [gridError, setGridError] = useState(false);
    const [loader, setLoader] = useState(true);

    const { organizationId } = useParams();

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        setLoader(true);

        const getDocumentsMine = async () => {
            const service = new ApproverService();
            const result = await service.getDocumentsMine(organizationId ? organizationId : "", true);
            if (result.hasErrors) {
                setGridError(true);
            } else {
                setGridError(false);
                if (isMounted) {
                    let list = []
                    list.push(...result.value.map((e, index) => {
                        const voucherDate: Date | null = e.voucherDate ? new Date(e.voucherDate) : null;
                        const dueDate: Date | null = e.dueDate ? new Date(e.dueDate) : null;
                        return {
                            id: index,
                            invoiceDate: voucherDate,
                            dueDate: dueDate,
                            amount: e.amount,
                        }
                    }))
                    setGridRows(list);
                }
            }
        }

        const getDocumentsAll = async () => {
            const service = new ApproverService();
            const result = await service.getDocumentsAll(organizationId ? organizationId : "", true);
            if (result.hasErrors) {
                setGridError(true);
            } else {
                setGridError(false);
                if (isMounted) {
                    let list = []
                    list.push(...result.value.map((e, index) => {
                        const voucherDate: Date | null = e.voucherDate ? new Date(e.voucherDate) : null;
                        const dueDate: Date | null = e.dueDate ? new Date(e.dueDate) : null;
                        return {
                            id: index,
                            invoiceDate: voucherDate,
                            dueDate: dueDate,
                            amount: e.amount,
                            assignedTo: e.assignedUserFullName,
                        }
                    }))
                    setGridRows(list);
                }
            }
        }

        if (props.type === "waitingYou")
            getDocumentsMine()
        else if (props.type === "waitingOthers")
            getDocumentsAll()
        setLoader(false);

        return () => { isMounted = false };

    }, [organizationId])

    const { t, i18n } = useTranslation();

    const columns: GridColDef[] = [
        {
            field: 'id', headerName: 'id', width: 60, hide: true,
            //renderCell: () => (<Check fontSize="large" color="success" />)
        },
        {
            field: 'invoiceDate', headerName: t('home.invoiceDate'), width: 105, sortable: true,
            renderCell: (params: GridCellParams) => params.value && params.value !== "" ? format(new Date(params.value), 'dd.MM.yyyy', { locale: nb }) : ""
        },
        {
            field: 'dueDate', headerName: t('search.dueDate'), width: 105, sortable: true,
            renderCell: (params: GridCellParams) => <DueDateWithColor params={params} />
        },
        {
            field: "amount", headerName: t('search.amount'), width: 130, align: "right",
            valueFormatter: (params: GridValueFormatterParams<number>) => {
                if (params.value == null) {
                    return "";
                }
                return new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value);
            }
        },
        {
            field: 'assignedTo',
            align: "center",
            headerName: t('basic.assignedTo'),
            width: 105,
            sortable: true,
            hide: props.type === "waitingOthers" ? false : true,
            flex: 1,
        },
    ];


    function CustomErrorOverlay() {
        return <Grid container direction="column" justifyContent="center" alignItems="center" height="100%">
            <Grid item><Error fontSize="large"/></Grid>
            <Grid item width="15rem" display="flex" justifyContent="center">
                <Typography variant="body2">
                    {t("basic.unexpectedError")}
                </Typography>
            </Grid>
        </Grid>
    }
    function CustomNoRowsOverlay() {
        return <Container sx={{ display: "flex", height:"100%", justifyContent:"center", alignItems:"center" }}>
            <Stack direction="column">
                <SearchOff fontSize="large" />
                <Typography>No documents!</Typography>
            </Stack>
        </Container>
    }

    return <DataGrid css={stripedDateGrid(theme, theme.palette.grey[200])}
        rows={gridRows}
        columns={columns}
        hideFooter
        rowHeight={remToPixels(2)}
        loading={loader}
        headerHeight={remToPixels(4)}
        getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        error={gridError ? true : undefined}
        components={{
            ErrorOverlay: CustomErrorOverlay,
            NoRowsOverlay: CustomNoRowsOverlay,
        }} />
}

export default DatagridWaitingInvoices;